import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Wrench, Calendar, X } from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { DateTimePicker } from '../../components/ui/DateTimePicker';
import { OrgInventoryItem, CasegoodsInventoryItem } from '../../types/admin';

// ── Types ───────────────────────────────────────────────────────────────────

interface AccessorialLineItem {
  label: string;
  hours: number;
  /** Number of inventory items contributing (only for dynamic items) */
  count?: number;
  source: 'building-protection' | 'modular' | 'inventory';
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/**
 * Compute accessorial line items for origin and destination.
 */
function computeAccessorialItems(
  inventory: CasegoodsInventoryItem[],
  orgItems: OrgInventoryItem[],
  formula: {
    truckloads?: number;
    modularDisassemblyManHours?: number;
    modularReassemblyManHours?: number;
    buildingProtectionManHours?: number;
  },
  moverDisassembling: boolean,
  moverReassembling: boolean,
): { origin: AccessorialLineItem[]; destination: AccessorialLineItem[] } {
  const origin: AccessorialLineItem[] = [];
  const destination: AccessorialLineItem[] = [];

  // 1. Building Protections — split 50/50
  const bpHours = formula.buildingProtectionManHours ?? Math.max(0, Math.ceil(formula.truckloads ?? 0) - 6);
  if (bpHours > 0) {
    const halfOrigin = bpHours / 2;
    const halfDest = bpHours / 2;
    origin.push({ label: 'Installing Building Protections', hours: halfOrigin, source: 'building-protection' });
    destination.push({ label: 'Installing Building Protections', hours: halfDest, source: 'building-protection' });
  }

  // 2. Modular Workstation Disassembly (origin) / Reassembly (destination)
  if (moverDisassembling && (formula.modularDisassemblyManHours ?? 0) > 0) {
    origin.push({
      label: 'Modular Workstation Disassembly',
      hours: formula.modularDisassemblyManHours!,
      source: 'modular',
    });
  }
  if (moverReassembling && (formula.modularReassemblyManHours ?? 0) > 0) {
    destination.push({
      label: 'Modular Workstation Reassembly',
      hours: formula.modularReassemblyManHours!,
      source: 'modular',
    });
  }

  // 3. Dynamic accessorial items — group by label
  const itemMap = new Map(orgItems.map(i => [i.id, i]));
  const originByLabel = new Map<string, { hours: number; count: number }>();
  const destByLabel = new Map<string, { hours: number; count: number }>();

  for (const inv of inventory) {
    const item = itemMap.get(inv.itemId);
    if (!item?.isAccessorial || !item.accessorialLabel) continue;
    const label = item.accessorialLabel;

    const originHrs = (item.accessorialOriginManHours ?? 0) * inv.quantity;
    if (originHrs > 0) {
      const existing = originByLabel.get(label) ?? { hours: 0, count: 0 };
      originByLabel.set(label, { hours: existing.hours + originHrs, count: existing.count + inv.quantity });
    }

    const destHrs = (item.accessorialDestinationManHours ?? 0) * inv.quantity;
    if (destHrs > 0) {
      const existing = destByLabel.get(label) ?? { hours: 0, count: 0 };
      destByLabel.set(label, { hours: existing.hours + destHrs, count: existing.count + inv.quantity });
    }
  }

  for (const [label, { hours, count }] of originByLabel) {
    origin.push({ label, hours, count, source: 'inventory' });
  }
  for (const [label, { hours, count }] of destByLabel) {
    destination.push({ label, hours, count, source: 'inventory' });
  }

  return { origin, destination };
}

// ── Line item row ───────────────────────────────────────────────────────────

function LineItemRow({ item }: { item: AccessorialLineItem }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-stone/50 last:border-0">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Wrench size={14} className="shrink-0 text-slate" />
        <div className="min-w-0">
          <p className="text-small text-midnight font-medium truncate">
            {item.label}
          </p>
          {item.count != null && (
            <p className="text-[11px] text-slate">
              {item.count} item{item.count !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
      <span className="shrink-0 text-small font-semibold text-midnight font-mono ml-3">
        {fmt(item.hours)}h
      </span>
    </div>
  );
}

// ── Section card ────────────────────────────────────────────────────────────

function SectionCard({
  title,
  items,
}: {
  title: string;
  items: AccessorialLineItem[];
}) {
  const total = items.reduce((sum, i) => sum + i.hours, 0);

  if (items.length === 0) {
    return (
      <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
        <h3 className="font-display text-body font-semibold text-midnight mb-2">{title}</h3>
        <p className="text-small text-slate">No accessorial services</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
      <h3 className="font-display text-body font-semibold text-midnight mb-1">{title}</h3>
      <div className="divide-y divide-stone/50">
        {items.map((item, idx) => (
          <LineItemRow key={`${item.label}-${idx}`} item={item} />
        ))}
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone">
        <span className="text-small font-semibold text-midnight">Total</span>
        <span className="text-small font-bold text-midnight font-mono">{fmt(total)}h</span>
      </div>
    </div>
  );
}

// ── Accessorial Services Date Card ──────────────────────────────────────────

function AccessorialDateCard({
  value,
  maxDate,
  onSave,
}: {
  value: string;
  maxDate: string | undefined;
  onSave: (v: string | null) => Promise<void>;
}) {
  // Normalize the value for DateTimePicker (needs YYYY-MM-DDTHH:MM format)
  const normalizedValue = (() => {
    if (!value) return '';
    const datePart = value.split('T')[0];
    const timePart = value.includes('T') ? value.split('T')[1] : '';
    return timePart ? `${datePart}T${timePart.substring(0, 5)}` : `${datePart}T09:00`;
  })();

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Track the "optimistic" value so the UI stays responsive between save and server refresh
  const [pendingValue, setPendingValue] = useState<string | null>(null);

  // The display value: use pending (user just changed) if set, otherwise server value
  const displayValue = pendingValue !== null ? pendingValue : normalizedValue;

  // Clear pending value once the server value catches up
  useEffect(() => {
    if (pendingValue !== null && normalizedValue === pendingValue) {
      setPendingValue(null);
    }
  }, [normalizedValue, pendingValue]);

  const handleChange = async (newValue: string) => {
    setSaveError('');
    setPendingValue(newValue);
    setSaving(true);
    try {
      await onSave(newValue || null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
      // Revert optimistic value on error
      setPendingValue(null);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => handleChange('');

  return (
    <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
      <div className="flex items-center gap-2 mb-2">
        <Calendar size={16} className="text-plum" />
        <h3 className="font-display text-body font-semibold text-midnight">Accessorial Services Date</h3>
        {saving && <span className="text-[11px] text-slate ml-auto">Saving…</span>}
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <DateTimePicker
            value={displayValue}
            onChange={handleChange}
            maxDate={maxDate}
            placeholder="Select date & time"
          />
        </div>

        {displayValue && (
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-rose hover:text-rose transition-colors duration-150"
            title="Clear date"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {saveError && (
        <p className="text-[11px] text-rose mt-2">{saveError}</p>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export function AccessorialServicesPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { quote, loading: quoteLoading, updateQuote, orgItems, orgItemsLoading } = useSharedQuote();

  if (quoteLoading || orgItemsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="p-4 text-center text-slate">
        <p>Quote not found.</p>
      </div>
    );
  }

  const formula = quote.formula;
  const inventory = quote.casegoodsInventory ?? [];
  const moverDisassembling = quote.modularWorkstations?.moverDisassembling ?? false;
  const moverReassembling = quote.modularWorkstations?.moverReassembling ?? false;

  const { origin, destination } = computeAccessorialItems(
    inventory,
    orgItems,
    {
      truckloads: formula?.truckloads,
      modularDisassemblyManHours: formula?.modularDisassemblyManHours,
      modularReassemblyManHours: formula?.modularReassemblyManHours,
      buildingProtectionManHours: formula?.buildingProtectionManHours,
    },
    moverDisassembling,
    moverReassembling,
  );

  const grandTotal = [...origin, ...destination].reduce((sum, i) => sum + i.hours, 0);

  return (
    <div className="px-4 pt-4 pb-8 md:max-w-4xl">
      {/* Mobile back */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight
                   mb-3 transition-colors duration-150 -ml-1"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Accessorial Services</h1>
      </div>

      {/* Date + sections grid: stacked on mobile, 2-col on md+ */}
      <div className="flex flex-col md:grid md:grid-cols-2 gap-4">
        {/* Accessorial Services Date (commercial only) — full width on md+ */}
        {quote.type !== 'residential' && (
          <div className="md:col-span-2">
            <AccessorialDateCard
              value={quote.accessorialServicesDate ?? ''}
              maxDate={quote.movingDate?.preferredDate ? quote.movingDate.preferredDate.split('T')[0] : undefined}
              onSave={async (v) => {
                await updateQuote({ accessorialServicesDate: v });
              }}
            />
          </div>
        )}

        {!formula && (
          <div className="md:col-span-2 flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-small text-amber-700">
              Formula has not been calculated yet. Complete the required fields to see accessorial service hours.
            </p>
          </div>
        )}

        <SectionCard title="Origin" items={origin} />
        <SectionCard title="Destination" items={destination} />

        {grandTotal > 0 && (
          <div className="md:col-span-2 flex items-center justify-between bg-plum/5 border border-plum/20 rounded-xl px-4 py-3">
            <span className="text-body font-semibold text-midnight">Grand Total</span>
            <span className="text-body font-bold text-plum font-mono">{fmt(grandTotal)}h</span>
          </div>
        )}
      </div>
    </div>
  );
}
